import { ApostleBase, ApostleLite } from '@apeiron/apostle'
import { CurrencyMoneyType } from '@apeiron/currency'
import { GodiverseLite } from '@apeiron/godiverse'
import { PlanetLite } from '@apeiron/planet'
import { StarLite } from '@apeiron/star'
import { GAParameters, MayNull, useGoogleTagManager } from '@apeiron/temp'
import { GAAssetType, GAEventCategory } from '@src/constants/googleAnalytics'
import usePriceCalculation from '@src/hooks/common/tools/usePriceCalculation'
import { useCallback } from 'react'

type GAApostleWithListing = ApostleBase & {
  listingInfo?: MayNull<ApostleLite['listingInfo']>
}

type FromType<T> = T extends GAAssetType.Planet
  ? MayNull<PlanetLite>
  : T extends GAAssetType.Star
  ? MayNull<StarLite>
  : T extends GAAssetType.Apostle
  ? MayNull<GAApostleWithListing>
  : T extends GAAssetType.Godiverse
  ? MayNull<GodiverseLite>
  : never

const useTracking = (): Hook => {
  const { getPriceInUSD } = usePriceCalculation()

  const { gtmEmitEvent } = useGoogleTagManager()

  const createGASingleAssetParams = useCallback(
    <T extends GAAssetType, U extends FromType<T>>(type: T, item: U) => {
      switch (type) {
        case GAAssetType.Planet: {
          // type is not narrowed, so we have to assert manually
          // related: https://stackoverflow.com/questions/68142672/conditional-type-that-narrows-a-union
          const planet = item as unknown as MayNull<PlanetLite>

          return {
            type,
            currency: CurrencyMoneyType.Usd,
            id: planet?.planetID,
            price: getPriceInUSD(planet?.listingInfo)
          }
        }

        case GAAssetType.Star:
        case GAAssetType.Apostle: {
          const starOrApostle = item as unknown as MayNull<
            StarLite | ApostleLite
          >

          return {
            type,
            currency: CurrencyMoneyType.Usd,
            id: starOrApostle?.id,
            price: getPriceInUSD(starOrApostle?.listingInfo)
          }
        }

        case GAAssetType.Godiverse: {
          const godiverse = item as unknown as MayNull<GodiverseLite>

          return {
            type,
            currency: CurrencyMoneyType.Usd,
            id: godiverse?.id,
            price: getPriceInUSD(godiverse?.cheapestListingInfo)
          }
        }

        default: {
          return {}
        }
      }
    },
    [getPriceInUSD]
  )

  const gtmEmitSelectItem = useCallback(
    ({ type, item }: EmitSingleItemParam) => {
      gtmEmitEvent(
        GAEventCategory.SelectItem,
        createGASingleAssetParams(type, item)
      )
    },
    [gtmEmitEvent, createGASingleAssetParams]
  )

  const gtmEmitViewItem = useCallback(
    ({ type, item }: EmitSingleItemParam) => {
      gtmEmitEvent(
        GAEventCategory.ViewItem,
        createGASingleAssetParams(type, item)
      )
    },
    [gtmEmitEvent, createGASingleAssetParams]
  )

  const gtmEmitAddToFavourite = useCallback(
    ({ type, item }: EmitSingleItemParam) => {
      gtmEmitEvent(
        GAEventCategory.AddToFavorite,
        createGASingleAssetParams(type, item)
      )
    },
    [gtmEmitEvent, createGASingleAssetParams]
  )

  const gtmEmitPurchase = useCallback(
    ({ type, item }: EmitSingleItemParam) => {
      gtmEmitEvent(
        GAEventCategory.Purchase,
        createGASingleAssetParams(type, item)
      )
    },
    [gtmEmitEvent, createGASingleAssetParams]
  )

  const gtmEmitViewItemList = useCallback(
    ({ type, items }: EmitViewItemListParam) => {
      switch (type) {
        case GAAssetType.Planet:
          gtmEmitEvent(GAEventCategory.ViewItemList, {
            type,
            items: items.map(item => ({
              item_id: item.planetID,
              item_price: getPriceInUSD(item.listingInfo),
              item_currency: CurrencyMoneyType.Usd
            }))
          })

          break
        case GAAssetType.Star:
        case GAAssetType.Apostle: {
          gtmEmitEvent(GAEventCategory.ViewItemList, {
            type,
            items: items.map(item => ({
              item_id: item.id,
              item_price: getPriceInUSD(item.listingInfo),
              item_currency: CurrencyMoneyType.Usd
            }))
          })

          break
        }

        case GAAssetType.Godiverse: {
          gtmEmitEvent(GAEventCategory.ViewItemList, {
            type,
            items: items.map(item => ({
              item_id: item.id,
              item_price: getPriceInUSD(item.cheapestListingInfo),
              item_currency: CurrencyMoneyType.Usd
            }))
          })

          break
        }
      }
    },
    [gtmEmitEvent, getPriceInUSD]
  )

  const gtmEmitFilter = useCallback(
    ({ type, label, value }: EmitFilterParam) => {
      gtmEmitEvent(GAEventCategory.Filter, {
        type,
        label,
        value
      })
    },
    [gtmEmitEvent]
  )

  const gtmHandleListFilterChange = useCallback(
    (
      type: GAAssetType,
      originalFilter: Record<string, any>,
      newFilter: Record<string, any>
    ) => {
      for (const key in newFilter) {
        if (typeof newFilter[key] !== 'object') {
          if (
            newFilter[key] !== originalFilter[key] &&
            newFilter[key] != null
          ) {
            const value = newFilter[key].toString()

            gtmEmitFilter({
              label: key,
              value,
              type
            })
          }
        } else if (Array.isArray(newFilter[key])) {
          for (const item of newFilter[key]) {
            if (!originalFilter[key] || !originalFilter[key].includes(item)) {
              const value = item.toString()

              gtmEmitFilter({
                label: key,
                value,
                type
              })
            }
          }
        }
      }
    },
    [gtmEmitFilter]
  )

  return {
    gtmEmitAddToFavourite,
    gtmEmitEvent,
    gtmEmitFilter,
    gtmEmitPurchase,
    gtmEmitSelectItem,
    gtmEmitViewItem,
    gtmEmitViewItemList,
    gtmHandleListFilterChange
  }
}

type Hook = {
  gtmEmitAddToFavourite: ({ type, item }: EmitSingleItemParam) => void
  gtmEmitEvent: (eventType: string, parameters: GAParameters) => void
  gtmEmitFilter: ({ type, label, value }: EmitFilterParam) => void
  gtmEmitPurchase: ({ type, item }: EmitSingleItemParam) => void
  gtmEmitSelectItem: ({ type, item }: EmitSingleItemParam) => void
  gtmEmitViewItem: ({ type, item }: EmitSingleItemParam) => void
  gtmEmitViewItemList: ({ type, items }: EmitViewItemListParam) => void
  gtmHandleListFilterChange: (
    type: GAAssetType,
    originalFilter: Record<string, any>,
    newFilter: Record<string, any>
  ) => void
}

type EmitSingleItemParam =
  | {
      type: GAAssetType.Apostle
      item?: MayNull<GAApostleWithListing>
    }
  | {
      type: GAAssetType.Planet
      item?: MayNull<PlanetLite>
    }
  | {
      type: GAAssetType.Star
      item?: MayNull<StarLite>
    }
  | {
      type: GAAssetType.Godiverse
      item?: MayNull<GodiverseLite>
    }

type EmitViewItemListParam =
  | {
      type: GAAssetType.Apostle
      items: GAApostleWithListing[]
    }
  | {
      type: GAAssetType.Planet
      items: PlanetLite[]
    }
  | {
      type: GAAssetType.Star
      items: StarLite[]
    }
  | {
      type: GAAssetType.Godiverse
      items: GodiverseLite[]
    }

type EmitFilterParam = {
  type: GAAssetType
  label: string
  value: string
}

export default useTracking
